/**
 * A list whose order is its meaning.
 *
 * Both rule lists short-circuit — the first rule to decide ends the stage — so
 * the position of a rule is not a display preference, it is what the rule does.
 * That is why the ordinal is shown as a number rather than implied by position,
 * and why reordering is a first-class action rather than a settings field.
 *
 * Draggable with a pointer, and operable without one: the handle is a button,
 * and arrow keys move the item it belongs to. A steward on a keyboard has the
 * same control as one with a mouse, which is not true of most drag lists.
 *
 * Locked items (the primary-key match rule) stay where they are and say so.
 */

import { useState } from 'react'

export interface ReorderableItem {
  id: string
  /** Locked items cannot be dragged and are never reordered around. */
  locked?: boolean
}

export function ReorderableList<T extends ReorderableItem>({
  items,
  onReorder,
  renderItem,
  disabled,
}: {
  items: T[]
  /** The complete list of ids in their new order. */
  onReorder: (orderedIds: string[]) => void
  renderItem: (item: T, index: number) => React.ReactNode
  disabled?: boolean
}) {
  const [dragging, setDragging] = useState<string | null>(null)
  const [over, setOver] = useState<string | null>(null)

  function reorder(from: number, to: number) {
    if (to < 0 || to >= items.length || from === to) return
    const next = [...items]
    const [moved] = next.splice(from, 1)
    next.splice(to, 0, moved)
    onReorder(next.map((i) => i.id))
  }

  return (
    <ol className="rl">
      {items.map((item, index) => (
        <li
          key={item.id}
          className="rl-item"
          data-dragging={dragging === item.id || undefined}
          data-over={over === item.id && dragging !== item.id ? true : undefined}
          onDragOver={(event) => {
            if (!dragging || disabled) return
            event.preventDefault()
            setOver(item.id)
          }}
          onDrop={(event) => {
            event.preventDefault()
            if (!dragging || disabled) return
            reorder(
              items.findIndex((i) => i.id === dragging),
              index,
            )
            setDragging(null)
            setOver(null)
          }}
        >
          <div className="rl-ordinal" aria-hidden="true">
            {index + 1}
          </div>

          <button
            type="button"
            className="rl-handle"
            aria-label={
              item.locked
                ? 'This rule is locked to first position'
                : `Reorder: item ${index + 1} of ${items.length}. Use the arrow keys to move it.`
            }
            disabled={disabled || item.locked}
            draggable={!disabled && !item.locked}
            onDragStart={() => setDragging(item.id)}
            onDragEnd={() => {
              setDragging(null)
              setOver(null)
            }}
            onKeyDown={(event) => {
              if (disabled || item.locked) return
              if (event.key === 'ArrowUp') {
                event.preventDefault()
                reorder(index, index - 1)
              } else if (event.key === 'ArrowDown') {
                event.preventDefault()
                reorder(index, index + 1)
              }
            }}
            title={item.locked ? 'Locked to first position' : 'Drag, or use the arrow keys'}
          >
            {item.locked ? '🔒' : '⠿'}
          </button>

          <div className="rl-body">{renderItem(item, index)}</div>
        </li>
      ))}
    </ol>
  )
}
